/**
 * Access model.
 *
 * Hierarchy: platform → organization (company, e.g. O'zbekgidroenergo) → tenant (an object such as a
 * reservoir, "suv ombori") → users.
 *
 *  - platform_admin  — "master admin" (the platform owner). Everything, everywhere.
 *  - org_admin       — company admin. Creates tenants in its organization and their admins; full access to
 *                      every tenant of the organization.
 *  - tenant_admin    — tenant (reservoir) admin. Builds pages, drawings, widgets; manages devices,
 *                      connectors, documents; creates tenant members.
 *  - tenant_member   — an employee ("xodim"). Access is described by a PermissionSet, edited in the
 *                      "Huquqlar" step of the employee form: an Admin panel (CRUD per module) and a
 *                      Monitoring panel (view / edit / delete / upload per menu). The default preset is a
 *                      read-only viewer.
 */
import { z } from 'zod';

export const ROLES = ['platform_admin', 'org_admin', 'tenant_admin', 'tenant_member'] as const;
export type Role = (typeof ROLES)[number];
export const roleSchema = z.enum(ROLES);

/** Admin panel ("Boshqaruv paneli - CRUD huquqlari"), in the order of the design (501:11683). */
export const ADMIN_MODULES = [
  'devices',
  'device_categories',
  'device_config',
  'employees',
  'connectors',
  'device_locations',
  'device_info',
  'device_calibration',
] as const;
export type AdminModule = (typeof ADMIN_MODULES)[number];
export const ADMIN_ACTIONS = ['create', 'read', 'update', 'delete'] as const;
export type AdminAction = (typeof ADMIN_ACTIONS)[number];

/** Monitoring panel ("Monitoring - ko'rish va fayl yuklash"), design order (501:12749). `edit` on dashboard/sections = constructor access. */
export const MONITORING_MENUS = [
  'dashboard',
  'device_config',
  'device_categories',
  'documents',
  'reports',
  'sections',
  'charts',
] as const;
export type MonitoringMenu = (typeof MONITORING_MENUS)[number];
export const MONITORING_ACTIONS = ['view', 'edit', 'delete', 'upload'] as const;
export type MonitoringAction = (typeof MONITORING_ACTIONS)[number];

export const permissionSetSchema = z.object({
  admin: z.object({
    enabled: z.boolean(),
    modules: z.partialRecord(z.enum(ADMIN_MODULES), z.array(z.enum(ADMIN_ACTIONS))),
  }),
  monitoring: z.object({
    enabled: z.boolean(),
    menus: z.partialRecord(z.enum(MONITORING_MENUS), z.array(z.enum(MONITORING_ACTIONS))),
  }),
});
export type PermissionSet = z.infer<typeof permissionSetSchema>;

/** Panel headers of the permission editor ("Huquqlar" step). */
export const PERMISSION_PANEL_LABELS = {
  admin: { title: 'Admin panel', subtitle: 'Boshqaruv paneli - CRUD huquqlari' },
  monitoring: { title: 'Monitoring panel', subtitle: "Monitoring - ko'rish va fayl yuklash" },
} as const;

/** Labels shown in the permission editor (Uzbek, as in the design). */
export const ADMIN_MODULE_LABELS: Record<AdminModule, string> = {
  devices: 'Qurilmalar',
  device_categories: 'Qurilma turkumlari',
  device_config: 'Qurilma konfiguratsiyasi',
  device_locations: "Qurilma joylashgan o'rni",
  device_info: "Qurilma haqida ma'lumot",
  device_calibration: 'Qurilma kolibrovkalash',
  employees: 'Xodimlar',
  connectors: 'Konnektorlar',
};
export const ADMIN_ACTION_LABELS: Record<AdminAction, string> = {
  create: 'Yaratish',
  read: "O'qish",
  update: 'Tahrirlash',
  delete: "O'chirish",
};
export const MONITORING_MENU_LABELS: Record<MonitoringMenu, string> = {
  dashboard: 'Bosh panel',
  sections: "Bo'limlar",
  device_config: 'Qurilma konfiguratsiyasi',
  device_categories: 'Qurilma turkumlari',
  documents: 'Hujjatlar',
  reports: 'Hisobotlar',
  charts: 'Grafiklar',
};
export const MONITORING_ACTION_LABELS: Record<MonitoringAction, string> = {
  view: "Ko'rinadi",
  edit: 'Tahrirlash',
  delete: "O'chirish",
  upload: 'Fayl yuklash',
};

export const EMPTY_PERMISSIONS: PermissionSet = {
  admin: { enabled: false, modules: {} },
  monitoring: { enabled: false, menus: {} },
};

/** Default for new employees: may look at everything, change nothing. */
export const VIEWER_PERMISSIONS: PermissionSet = {
  admin: { enabled: false, modules: {} },
  monitoring: {
    enabled: true,
    menus: Object.fromEntries(MONITORING_MENUS.map((m) => [m, ['view']])) as PermissionSet['monitoring']['menus'],
  },
};

export const FULL_PERMISSIONS: PermissionSet = {
  admin: {
    enabled: true,
    modules: Object.fromEntries(ADMIN_MODULES.map((m) => [m, [...ADMIN_ACTIONS]])) as PermissionSet['admin']['modules'],
  },
  monitoring: {
    enabled: true,
    menus: Object.fromEntries(MONITORING_MENUS.map((m) => [m, [...MONITORING_ACTIONS]])) as PermissionSet['monitoring']['menus'],
  },
};

export type Permission =
  | { panel: 'admin'; module: AdminModule; action: AdminAction }
  | { panel: 'monitoring'; menu: MonitoringMenu; action: MonitoringAction };

export interface MembershipGrant {
  role: Role;
  organizationId: string | null;
  tenantId: string | null;
  permissions: PermissionSet | null;
}

/** What the API knows about the current user (also returned by GET /auth/me). */
export interface Principal {
  userId: string;
  memberships: MembershipGrant[];
}

/** A tenant reference with its owning organization, needed to evaluate org-level grants. */
export interface TenantRef {
  id: string;
  organizationId: string;
}

export const isPlatformAdmin = (p: Principal) => p.memberships.some((m) => m.role === 'platform_admin');

export const isOrgAdmin = (p: Principal, organizationId: string) =>
  isPlatformAdmin(p) || p.memberships.some((m) => m.role === 'org_admin' && m.organizationId === organizationId);

export const isTenantAdmin = (p: Principal, tenant: TenantRef) =>
  isOrgAdmin(p, tenant.organizationId) ||
  p.memberships.some((m) => m.role === 'tenant_admin' && m.tenantId === tenant.id);

/** Effective permission set of a principal inside a tenant (null = no access at all). */
export function effectivePermissions(p: Principal, tenant: TenantRef): PermissionSet | null {
  if (isTenantAdmin(p, tenant)) return FULL_PERMISSIONS;
  const member = p.memberships.find((m) => m.role === 'tenant_member' && m.tenantId === tenant.id);
  if (!member) return null;
  return member.permissions ?? VIEWER_PERMISSIONS;
}

export function hasPermission(set: PermissionSet | null, perm: Permission): boolean {
  if (!set) return false;
  if (perm.panel === 'admin') {
    return set.admin.enabled && (set.admin.modules[perm.module]?.includes(perm.action) ?? false);
  }
  return set.monitoring.enabled && (set.monitoring.menus[perm.menu]?.includes(perm.action) ?? false);
}

export const can = (p: Principal, tenant: TenantRef, perm: Permission) =>
  hasPermission(effectivePermissions(p, tenant), perm);

/** True when `child` grants nothing beyond `parent` — used to stop privilege escalation when members manage members. */
export function isSubsetOf(child: PermissionSet, parent: PermissionSet): boolean {
  for (const [module, actions] of Object.entries(child.admin.modules) as [AdminModule, AdminAction[]][]) {
    if (actions.length && !parent.admin.enabled) return false;
    if (actions.some((a) => !parent.admin.modules[module]?.includes(a))) return false;
  }
  for (const [menu, actions] of Object.entries(child.monitoring.menus) as [MonitoringMenu, MonitoringAction[]][]) {
    if (actions.length && !parent.monitoring.enabled) return false;
    if (actions.some((a) => !parent.monitoring.menus[menu]?.includes(a))) return false;
  }
  return true;
}

/** Which roles a principal may assign when creating/updating a user in the given scope. */
export function assignableRoles(p: Principal, scope: { organizationId: string; tenant?: TenantRef }): Role[] {
  if (isPlatformAdmin(p)) return [...ROLES];
  if (isOrgAdmin(p, scope.organizationId)) return ['org_admin', 'tenant_admin', 'tenant_member'];
  if (scope.tenant && isTenantAdmin(p, scope.tenant)) return ['tenant_member'];
  if (scope.tenant && can(p, scope.tenant, { panel: 'admin', module: 'employees', action: 'create' })) {
    return ['tenant_member'];
  }
  return [];
}
